import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  MessageCircle, 
  HelpCircle, 
  Settings, 
  LogOut,
  Sparkles,
  Shield,
  Zap,
  Sliders,
  Wand2,
  LayoutGrid,
  ChevronRight,
  User
} from 'lucide-react';
import { authService } from '../services/api';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.logout();
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('isAuthenticated');
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // Mesmo com erro, limpar localStorage e redirecionar
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('isAuthenticated');
      navigate('/login');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-5, 5, -5],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-sand via-white to-brand-sand/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-brand-slate/5 to-brand-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-brand-accent/5 to-brand-slate/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-brand-slate/3 to-brand-accent/5 rounded-full blur-3xl"></div>
      </div>

      <section className="section pt-32 min-h-screen relative z-10">
        <div className="wrapper">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-7xl mx-auto"
          >
            {/* Header */}
            <motion.div variants={itemVariants} className="text-center mb-16">
              <motion.div 
                variants={floatingVariants}
                animate="animate"
                className="w-24 h-24 bg-gradient-to-br from-brand-slate via-brand-accent to-brand-slate rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-3xl"></div>
                <Settings className="w-12 h-12 text-white relative z-10" />
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 rounded-3xl border-2 border-white/20"
                ></motion.div>
              </motion.div>
              
              <motion.h1 
                variants={itemVariants}
                className="text-5xl md:text-6xl font-heading text-brand-slate mb-6 bg-gradient-to-r from-brand-slate to-brand-accent bg-clip-text text-transparent"
              >
                Painel Administrativo
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className="text-xl text-brand-ink/80 max-w-3xl mx-auto leading-relaxed"
              >
                Gerencie todo o conteúdo do site com facilidade e eficiência
              </motion.p>

              {/* Badges */}
              <motion.div 
                variants={itemVariants}
                className="flex flex-wrap justify-center gap-8 mt-12"
              >
                <div className="flex items-center gap-3 bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-lg">
                  <Shield className="w-5 h-5 text-brand-slate" />
                  <span className="text-brand-slate font-semibold">Sistema Seguro</span>
                </div>
                <div className="flex items-center gap-3 bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-lg">
                  <Zap className="w-5 h-5 text-brand-accent" />
                  <span className="text-brand-slate font-semibold">Interface Rápida</span>
                </div>
                <div className="flex items-center gap-3 bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-lg">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  <span className="text-brand-slate font-semibold">Design Moderno</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Toolbar */}
            <motion.div variants={itemVariants} className="mb-10 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-brand-ink/70">
                <LayoutGrid className="w-5 h-5" />
                <span>Área logada</span>
                <ChevronRight className="w-4 h-4" />
                <span className="text-brand-slate font-semibold">Painel</span>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/admin/profile" className="btn-secondary inline-flex items-center gap-2">
                  <User className="w-4 h-4" /> Meu Perfil
                </Link>
                <a href="/admin/settings" className="btn-secondary inline-flex items-center gap-2">
                  <Sliders className="w-4 h-4" /> Configurações
                </a>
                <button className="btn-secondary inline-flex items-center gap-2">
                  <Wand2 className="w-4 h-4" /> Ações rápidas
                </button>
              </div>
            </motion.div>

            {/* Management Cards */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {/* Blog Management */}
              <Link to="/admin/blog" className="group no-underline">
                <motion.div 
                  whileHover={{ y: -8, scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 h-full group-hover:shadow-2xl transition-all duration-500 border border-white/50 overflow-hidden ring-1 ring-black/5"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all duration-300 shadow-md"
                    >
                      <BookOpen className="w-7 h-7 text-white" />
                    </motion.div>
                    
                    <h3 className="text-lg font-heading text-brand-slate mb-2 group-hover:text-blue-600 transition-colors duration-300">
                      Gerenciar Blog
                    </h3>
                    {/* ações removidas a pedido */}
                  </div>
                </motion.div>
              </Link>

              {/* Testimonials Management */}
              <Link to="/admin/testimonials" className="group no-underline">
                <motion.div 
                  whileHover={{ y: -8, scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 h-full group-hover:shadow-2xl transition-all duration-500 border border-white/50 overflow-hidden ring-1 ring-black/5"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-green-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all duration-300 shadow-md"
                    >
                      <MessageCircle className="w-7 h-7 text-white" />
                    </motion.div>
                    
                    <h3 className="text-lg font-heading text-brand-slate mb-2 group-hover:text-green-600 transition-colors duration-300">
                      Gerenciar Depoimentos
                    </h3>
                    {/* ações removidas a pedido */}
                  </div>
                </motion.div>
              </Link>

              {/* FAQ Management */}
              <Link to="/admin/faq" className="group no-underline">
                <motion.div 
                  whileHover={{ y: -8, scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 h-full group-hover:shadow-2xl transition-all duration-500 border border-white/50 overflow-hidden ring-1 ring-black/5"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:shadow-lg transition-all duration-300 shadow-md"
                    >
                      <HelpCircle className="w-7 h-7 text-white" />
                    </motion.div>
                    
                    <h3 className="text-lg font-heading text-brand-slate mb-2 group-hover:text-purple-600 transition-colors duration-300">
                      Gerenciar FAQ
                    </h3>
                    {/* ações removidas a pedido */}
                  </div>
                </motion.div>
              </Link>

              {/* Settings Management removed by request */}
            </motion.div>

            {/* Content Preview Cards removed by request */}

            {/* Quick Actions */}
            <motion.div variants={itemVariants} className="mb-10">
              <h4 className="text-brand-slate font-heading text-xl mb-4">Ações rápidas</h4>
              <div className="flex flex-wrap gap-3">
                <Link to="/admin/blog" className="btn-secondary">Novo artigo</Link>
                <Link to="/admin/testimonials" className="btn-secondary">Novo depoimento</Link>
                <Link to="/admin/faq" className="btn-secondary">Nova pergunta</Link>
                <Link to="/admin/settings" className="btn-secondary">Editar configurações</Link>
              </div>
            </motion.div>

            {/* Logout Button */}
            <motion.div 
              variants={itemVariants} 
              className="text-center"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleLogout}
                className="group bg-white/80 backdrop-blur-sm hover:bg-red-50 border border-white/50 hover:border-red-200 text-brand-slate hover:text-red-600 rounded-2xl px-8 py-4 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    whileHover={{ rotate: 12 }}
                    transition={{ duration: 0.2 }}
                  >
                    <LogOut className="w-5 h-5" />
                  </motion.div>
                  <span>Sair do sistema</span>
                </div>
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;